if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        // parse product JSON injected from buy-buttons.liquid
        const productJsonEl = this.querySelector('.product-json');
        try {
          this.product = productJsonEl ? JSON.parse(productJsonEl.textContent) : {};
        } catch (e) {
          this.product = {};
          console.error('Failed to parse product JSON for product-form', e);
        }

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // find the dynamic checkout wrapper we added in Liquid
        this.dynamicCheckoutWrapper = this.querySelector('.product-form__dynamic-checkout');

        // initialize variant change handling
        this.initVariantChangeHandler();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings
            ? window.variantStrings.addToCart
            : 'Add to cart';
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      /* ========== New: variant-handling ========== */
      initVariantChangeHandler() {
        // update once at initialization
        this._prevVariantId = this.variantIdInput ? this.variantIdInput.value : null;
        this.updateButtonsForVariantId(this._prevVariantId);

        // listen for form-level changes
        this.form.addEventListener('change', () => {
          const id = this.variantIdInput ? this.variantIdInput.value : null;
          this._prevVariantId = id;
          this.updateButtonsForVariantId(id);
        });

        // listen for custom events from other scripts
        document.addEventListener('variant:change', (e) => {
          if (e?.detail?.variant) this.updateButtonsForVariant(e.detail.variant);
        });
        document.addEventListener('variant:changed', (e) => {
          if (e?.detail?.variant) this.updateButtonsForVariant(e.detail.variant);
        });

        // fallback polling
        this._pollInterval = setInterval(() => {
          const id = this.variantIdInput ? this.variantIdInput.value : null;
          if (id !== this._prevVariantId) {
            this._prevVariantId = id;
            this.updateButtonsForVariantId(id);
          }
        }, 250);
      }

      updateButtonsForVariantId(variantId) {
        if (!variantId) {
          this.applyAvailability(false);
          return;
        }
        const variantObj =
          (this.product &&
            this.product.variants &&
            this.product.variants.find((v) => String(v.id) === String(variantId))) ||
          null;
        const available = variantObj ? Boolean(variantObj.available) : false;
        this.applyAvailability(available);
      }

      updateButtonsForVariant(variantOrBool) {
        if (typeof variantOrBool === 'boolean') {
          this.applyAvailability(variantOrBool);
          return;
        }
        const variantObj = variantOrBool && variantOrBool.id ? variantOrBool : null;
        const available = variantObj ? Boolean(variantObj.available) : false;
        this.applyAvailability(available);
      }

      applyAvailability(isAvailable) {
        // Add-to-cart button
        if (!isAvailable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (this.submitButtonText)
            this.submitButtonText.textContent = window.variantStrings
              ? window.variantStrings.soldOut
              : 'Sold out';
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText)
            this.submitButtonText.textContent = window.variantStrings
              ? window.variantStrings.addToCart
              : 'Add to cart';
        }

        // Dynamic checkout (Buy Now)
        if (this.dynamicCheckoutWrapper) {
          if (isAvailable) {
            this.dynamicCheckoutWrapper.removeAttribute('hidden');
          } else {
            this.dynamicCheckoutWrapper.setAttribute('hidden', '');
          }
        }
      }
    }
  );
}
